import React, { useEffect } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { Button } from 'reactstrap';
import { MUSIC24_MONTHLY_PRICE_USD, VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import type { ShowcaseUpsellAction } from './showcaseActions';
import { trackShowcaseUpsellShown } from './showcaseAnalytics';
import { showcaseMessages } from './showcaseMessages';
import { ShowcaseSignUpLink } from './ShowcaseSignUpLink';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import editorStyles from '../MusicVideoEditor.module.scss';
import styles from './MusicVideoShowcase.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type ShowcaseLockedFootageTab = 'ai' | 'yours' | 'upload';

interface LockedCopy {
  action: ShowcaseUpsellAction;
  title: MessageDescriptor;
  line: MessageDescriptor;
  hint: MessageDescriptor;
}

const LOCKED_COPY: Readonly<Record<ShowcaseLockedFootageTab, LockedCopy>> = {
  ai: {
    action: 'aiClips',
    title: showcaseMessages.aiLead,
    line: showcaseMessages.aiRest,
    hint: showcaseMessages.aiHint,
  },
  yours: {
    action: 'yours',
    title: showcaseMessages.footageLead,
    line: showcaseMessages.yoursLine,
    hint: showcaseMessages.yoursHint,
  },
  upload: {
    action: 'upload',
    title: showcaseMessages.footageLead,
    line: showcaseMessages.uploadLine,
    hint: showcaseMessages.uploadHint,
  },
};

/** The faded clips under the card: stand-ins, not data (design c3). */
const GHOST_CLIPS = [0, 1, 2, 3];

interface ShowcaseLockedTabProps {
  tab: ShowcaseLockedFootageTab;
  onBackToBank: () => void;
}

/**
 * The AI, Yours and Upload tabs in showcase mode (design c3). The tabs stay visible, so the visitor
 * sees what an account adds. A tab shows the sign-up card in the panel, not as a dialog: a tab click
 * is browsing, not an attempt to make. "Back to the bank" selects the Bank tab.
 */
const ShowcaseLockedTabComponent: React.FC<ShowcaseLockedTabProps> = ({ tab, onBackToBank }) => {
  const intl = useIntl();
  const copy = LOCKED_COPY[tab];
  const values = {
    credits: VIDEO_CREDIT_COST.aiClip,
    price: intl.formatNumber(MUSIC24_MONTHLY_PRICE_USD.artist, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }),
  };
  useEffect(() => {
    trackShowcaseUpsellShown(copy.action);
  }, [copy.action]);

  return (
    <>
      <div className={styles.panelCard}>
        <span className={styles.chip}>{intl.formatMessage(showcaseMessages.lockedChip)}</span>
        <b>{intl.formatMessage(copy.title, values)}</b>
        <p>{intl.formatMessage(copy.line, values)}</p>
        <div className={editorStyles.row}>
          <ShowcaseSignUpLink place={copy.action}>{intl.formatMessage(showcaseMessages.startFree)}</ShowcaseSignUpLink>
          <Button color="secondary" outline size="sm" onClick={onBackToBank}>
            {intl.formatMessage(showcaseMessages.backToBank)}
          </Button>
        </div>
      </div>
      <div className={styles.ghostClips} aria-hidden>
        {GHOST_CLIPS.map((index) => (
          <span key={index} className={styles.ghostClip} />
        ))}
      </div>
      <span className={editorStyles.grow} />
      <p className={editorStyles.hint}>{intl.formatMessage(copy.hint, values)}</p>
    </>
  );
};

export const ShowcaseLockedTab = TypedMemo(ShowcaseLockedTabComponent);
